export const TAG_KEY = {
  METHOD: 'method',
  PATH: 'path',
  STATUS_CODE: 'status_code',

  ROUTE: 'route',
  OUTCOME: 'outcome',
  VALIDATION: 'validation',
  COMPONENT: 'component',

  AGENT_ID: 'agent_id',
  AUTH: 'auth',
  STREAM_OUTCOME: 'stream_outcome',
  STAGE: 'stage',
  ATTEMPT: 'attempt',
  KIND: 'kind',
  RESULT: 'result',
  DETAIL: 'detail',
  NEGATION: 'negation',
} as const;

export type TagKey = (typeof TAG_KEY)[keyof typeof TAG_KEY];

export const AUTH_TAG = {
  AUTHENTICATED: 'authenticated',
  ANONYMOUS: 'anonymous',
} as const;

export type AuthTag = (typeof AUTH_TAG)[keyof typeof AUTH_TAG];

/** Low-cardinality `outcome` tag values for `app.*` error metrics. */
export const APP_ERROR_OUTCOME = {
  REACT_BOUNDARY: 'react_boundary',
  WINDOW_ERROR: 'window_error',
  UNHANDLED_REJECTION: 'unhandled_rejection',
  SERVER: 'server',
} as const;

export type AppErrorOutcome = (typeof APP_ERROR_OUTCOME)[keyof typeof APP_ERROR_OUTCOME];

export const METRIC_NAME = {
  AI_REVIEW_REQUEST_COUNT: 'ai_review.request.count',
  AI_REVIEW_REQUEST_DURATION_MS: 'ai_review.request.duration_ms',
  AI_REVIEW_REQUEST_ERROR_COUNT: 'ai_review.request.error_count',

  PUBLIC_API_REQUEST_COUNT: 'public_api.request.count',
  PUBLIC_API_REQUEST_DURATION_MS: 'public_api.request.duration_ms',
  PUBLIC_API_REQUEST_ERROR_COUNT: 'public_api.request.error_count',

  AI_CHAT_REQUEST_COUNT: 'ai_chat.request.count',
  AI_CHAT_REQUEST_DURATION_MS: 'ai_chat.request.duration_ms',
  AI_CHAT_PRE_STREAM_DURATION_MS: 'ai_chat.pre_stream.duration_ms',
  AI_CHAT_STREAM_DURATION_MS: 'ai_chat.stream.duration_ms',
  AI_CHAT_SLOT_REPLACED_PREVIOUS: 'ai_chat.slot.replaced_previous',

  AI_PERSONA_CHAT_REQUEST_COUNT: 'ai_persona_chat.request.count',
  AI_PERSONA_CHAT_REQUEST_DURATION_MS: 'ai_persona_chat.request.duration_ms',
  AI_PERSONA_CHAT_PRE_STREAM_DURATION_MS: 'ai_persona_chat.pre_stream.duration_ms',
  AI_PERSONA_CHAT_STREAM_DURATION_MS: 'ai_persona_chat.stream.duration_ms',
  AI_PERSONA_CHAT_SLOT_REPLACED_PREVIOUS: 'ai_persona_chat.slot.replaced_previous',
  AI_PERSONA_CHAT_TWIN_FAILURE_COUNT: 'ai_persona_chat.twin.failure.count',
  AI_PERSONA_CHAT_TWIN_TRUNCATED_COUNT: 'ai_persona_chat.twin.truncated.count',
  AI_PERSONA_CHAT_FAN_OUT_DURATION_MS: 'ai_persona_chat.fan_out.duration_ms',

  AI_INSIGHTS_CHAT_REQUEST_COUNT: 'ai_insights_chat.request.count',
  AI_INSIGHTS_CHAT_REQUEST_DURATION_MS: 'ai_insights_chat.request.duration_ms',
  AI_INSIGHTS_CHAT_PRE_STREAM_DURATION_MS: 'ai_insights_chat.pre_stream.duration_ms',
  AI_INSIGHTS_CHAT_STREAM_DURATION_MS: 'ai_insights_chat.stream.duration_ms',
  AI_INSIGHTS_CHAT_SLOT_REPLACED_PREVIOUS: 'ai_insights_chat.slot.replaced_previous',

  AI_SCREENER_REQUEST_COUNT: 'ai_screener.request.count',
  AI_SCREENER_REQUEST_DURATION_MS: 'ai_screener.request.duration_ms',
  AI_SCREENER_REQUEST_ERROR_COUNT: 'ai_screener.request.error_count',

  AI_SCREENER_DQ_REQUEST_COUNT: 'ai_screener_dq.request.count',
  AI_SCREENER_DQ_REQUEST_DURATION_MS: 'ai_screener_dq.request.duration_ms',
  AI_SCREENER_DQ_REQUEST_ERROR_COUNT: 'ai_screener_dq.request.error_count',

  AI_SCREENER_VARIABLES_REQUEST_COUNT: 'ai_screener_variables.request.count',
  AI_SCREENER_VARIABLES_REQUEST_DURATION_MS: 'ai_screener_variables.request.duration_ms',
  AI_SCREENER_VARIABLES_REQUEST_ERROR_COUNT: 'ai_screener_variables.request.error_count',

  /**
   * Shared with .NET's `Profiles.CohortFilter` meter (`lib/Profiles/CohortFilterTimer.cs`), so the
   * cohort-filter dashboard reads both. Recorded with .NET's buckets; see `otel.ts`.
   */
  COHORT_FILTER_DURATION: 'cohort_filter.duration',

  // Twin-search: timings (latency) + percents (ratios and scores), all histograms.
  TWIN_SEARCH_REQUEST_DURATION_MS: 'twin_search.request.duration_ms',
  TWIN_SEARCH_REQUEST_PRELUDE_DURATION_MS: 'twin_search.request.prelude_duration_ms',
  TWIN_SEARCH_STAGE_DURATION_MS: 'twin_search.stage.duration_ms',
  TWIN_SEARCH_SPLIT_ATTEMPT_DURATION_MS: 'twin_search.split.attempt.duration_ms',
  TWIN_SEARCH_JUDGE_CALL_DURATION_MS: 'twin_search.judge.call.duration_ms',
  /** The parallel audience-name call, tagged by outcome (ok / invalid / error). */
  TWIN_SEARCH_NAME_CALL_DURATION_MS: 'twin_search.name.call.duration_ms',
  /** Share of judged candidates with llmScore >= 0 (0–100). */
  TWIN_SEARCH_JUDGE_PASS_RATE: 'twin_search.judge.pass_rate',
  /** Mean llmScore of returned twins (0–100); failed judges count as 0. */
  TWIN_SEARCH_MATCH_MEAN_LLM_SCORE: 'twin_search.match.mean_llm_score',
  /** Share of returned twins with any contradicted facet (0–100). */
  TWIN_SEARCH_MATCH_CONTRADICTION_RATE: 'twin_search.match.contradiction_rate',

  APP_REQUEST_ERROR_COUNT: 'app.request.error_count',
  APP_CLIENT_ERROR_COUNT: 'app.client.error_count',
} as const;

export type MetricName = (typeof METRIC_NAME)[keyof typeof METRIC_NAME];

/** Metrics the browser may increment via `/api/report-metric`. */
export const CLIENT_METRIC_NAME = {
  AI_SUMMARY_THUMBS_UP: 'app.client.ai_summary.thumbs_up_count',
  AI_SUMMARY_THUMBS_DOWN: 'app.client.ai_summary.thumbs_down_count',
  PERSONA_CHAT_THUMBS_UP: 'app.client.persona_chat.thumbs_up_count',
  PERSONA_CHAT_THUMBS_DOWN: 'app.client.persona_chat.thumbs_down_count',
} as const;

export type ClientMetricName = (typeof CLIENT_METRIC_NAME)[keyof typeof CLIENT_METRIC_NAME];

export const CLIENT_REPORTABLE_METRICS = new Set<string>(Object.values(CLIENT_METRIC_NAME));

/** Low-cardinality component value for AI summary feedback metrics. */
export const AI_SUMMARY_FEEDBACK_COMPONENT = 'ai-recommendations-summary' as const;

export type AiSummaryFeedbackComponent = typeof AI_SUMMARY_FEEDBACK_COMPONENT;

export const AI_SUMMARY_FEEDBACK_COMPONENTS = new Set<string>([AI_SUMMARY_FEEDBACK_COMPONENT]);

/** Low-cardinality component value for persona chat feedback metrics. */
export const PERSONA_CHAT_FEEDBACK_COMPONENT = 'persona-chat' as const;

export type PersonaChatFeedbackComponent = typeof PERSONA_CHAT_FEEDBACK_COMPONENT;

export const PERSONA_CHAT_FEEDBACK_COMPONENTS = new Set<string>([PERSONA_CHAT_FEEDBACK_COMPONENT]);

/** Tag keys permitted on client-reportable metrics (expand deliberately per metric). */
export const CLIENT_REPORTABLE_TAG_KEYS = new Set<string>([TAG_KEY.COMPONENT]);
